import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Customer, CustomerContact, CustomerCreate, CustomerUpdate } from '../models/Customer';
import { CustomerAttachment, CustomerAttachmentCreate, CustomerAttachmentUpdate } from '../models/CustomerAttachment';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
}

export interface PagedResult<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export type CustomerPagedResult = PagedResult<Customer>;

@Injectable({ providedIn: 'root' })
export class CustomersService {
  // Usa sempre o endpoint real do microserviço comercial (sem passar pelo /api local)
  private baseUrl = environment.microservices.customer;
  private get blobBaseUrl() { return `${this.baseUrl}/blobcustomer`; }

  constructor(private http: HttpClient, private errorService: ErrorService) {}

  

  private buildPaginationParams(options?: PaginationOptions & { sortBy?: string; sortDirection?: string }): HttpParams {
    let params = new HttpParams();
    if (options?.page) {
      params = params.set('Page', options.page.toString());
    }
    if (options?.pageSize) {
      params = params.set('PageSize', options.pageSize.toString());
    }
    if (options?.sortBy) {
      params = params.set('sortBy', options.sortBy).set('sortDirection', options.sortDirection || 'asc');
    }
    return params;
  }

  /**
   * Retorna apenas o array de clientes (compatibilidade)
   */
  getAll(options?: PaginationOptions): Observable<Customer[]> {
    return this.getAllPaged(options).pipe(
      map(result => result.data)
    );
  }

  /**
   * Retorna resultado paginado completo com metadados
   */
  getAllPaged(options?: PaginationOptions & { status?: string; category?: string; searchTerm?: string; personType?: string; sortBy?: string; sortDirection?: string }): Observable<CustomerPagedResult> {
    let params = this.buildPaginationParams(options);
    // Garantir que sortBy/sortDirection sejam sempre enviados quando existirem
    if (options?.sortBy) {
      params = params.set('sortBy', options.sortBy).set('sortDirection', options.sortDirection || 'asc');
    }
    
    // Adiciona novos filtros se fornecidos
    if (options?.status) {
      params = params.set('status', options.status);
    }
    if (options?.category) {
      params = params.set('category', options.category);
    }
    if (options?.searchTerm) {
      params = params.set('searchTerm', options.searchTerm);
    }
    if (options?.personType) {
      params = params.set('personType', options.personType);
    }
    
    return this.http.get<any>(`${this.baseUrl}/customers`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes'); return throwError(() => err); })
    );
  }

  getById(id: number): Observable<Customer> {
    return this.http.get<any>(`${this.baseUrl}/customers/${id}`).pipe(
      map(c => this.mapFromCustomerDTO(this.extractCustomerPayload(c))),
      catchError(err => { this.errorService.showError('Erro ao buscar cliente'); return throwError(() => err); })
    );
  }

  create(payload: CustomerCreate): Observable<Customer> {
    const dto = this.mapToCustomerDTO(payload);
    return this.http.post<any>(`${this.baseUrl}/customers`, dto).pipe(
      map((res: any) => {
        const message = res?.message || 'Cliente criado com sucesso';
        this.errorService.showSuccessMessage(message);
        return res;
      }),
      catchError(err => { this.errorService.showError('Erro ao criar cliente'); return throwError(() => err); })
    );
  }

  update(id: number, payload: CustomerUpdate): Observable<void> {
    const dto = this.mapToCustomerDTO(payload);
    return this.http.put<void>(`${this.baseUrl}/customers/${id}`, dto).pipe(
      catchError(err => { this.errorService.showError('Erro ao atualizar cliente'); return throwError(() => err); })
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/customers/${id}`).pipe(
      catchError(err => { this.errorService.showError('Erro ao excluir cliente'); return throwError(() => err); })
    );
  }

  // Filtros comuns
  getActive(options?: PaginationOptions): Observable<Customer[]> {
    return this.getActivePaged(options).pipe(
      map(result => result.data)
    );
  }

  getActivePaged(options?: PaginationOptions): Observable<CustomerPagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(`${this.baseUrl}/customers/active`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes ativos'); return throwError(() => err); })
    );
  }

  getByDocument(documentNumber: string): Observable<Customer | null> {
    return this.http.get<any>(`${this.baseUrl}/customers/document/${encodeURIComponent(documentNumber)}`).pipe(
      map(c => {
        if (c?.error && (c.error.includes('não encontrado') || c.error.includes('nao encontrado') || c.error.includes('n\u00E3o encontrado'))) {
          return null;
        }
        return c ? this.mapFromCustomerDTO(c) : null;
      }),
      catchError(err => {
        if (err.status === 404 ||
            err.error?.error?.includes('não encontrado') ||
            err.error?.error?.includes('nao encontrado') ||
            err.error?.error?.includes('n\u00E3o encontrado')) {
          return of(null);
        }
        console.error('Erro ao buscar cliente por documento:', err);
        this.errorService.showError('Erro ao buscar cliente por documento');
        return throwError(() => err);
      })
    );
  }

  getByStateRegistration(stateRegistration: string): Observable<Customer | null> {
    return this.http.get<any>(`${this.baseUrl}/customers/stateregistration/${encodeURIComponent(stateRegistration)}`).pipe(
      map(c => {
        if (c?.error && (c.error.includes('não encontrado') || c.error.includes('nao encontrado') || c.error.includes('n\u00E3o encontrado'))) {
          return null;
        }
        return c ? this.mapFromCustomerDTO(c) : null;
      }),
      catchError(err => {
        if (err.status === 404 ||
            err.error?.error?.includes('não encontrado') ||
            err.error?.error?.includes('nao encontrado') ||
            err.error?.error?.includes('n\u00E3o encontrado')) {
          return of(null);
        }
        console.error('Erro ao buscar cliente por RG/Inscrição Estadual:', err);
        this.errorService.showError('Erro ao buscar cliente por RG/Inscrição Estadual');
        return throwError(() => err);
      })
    );
  }

  searchByCompanyName(name: string, options?: PaginationOptions): Observable<Customer[]> {
    return this.searchByCompanyNamePaged(name, options).pipe(
      map(result => result.data)
    );
  }

  searchByCompanyNamePaged(name: string, options?: PaginationOptions): Observable<CustomerPagedResult> {
    let params = new HttpParams().set('name', name);
    if (options?.page) {
      params = params.set('page', options.page.toString());
    }
    if (options?.pageSize) {
      params = params.set('pageSize', options.pageSize.toString());
    }
    return this.http.get<any>(`${this.baseUrl}/customers/search/company`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes por nome'); return throwError(() => err); })
    );
  }

  getByCategory(category: string, options?: PaginationOptions): Observable<Customer[]> {
    return this.getByCategoryPaged(category, options).pipe(
      map(result => result.data)
    );
  }

  getByCategoryPaged(category: string, options?: PaginationOptions): Observable<CustomerPagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(`${this.baseUrl}/customers/category/${encodeURIComponent(category)}`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes por categoria'); return throwError(() => err); })
    );
  }

  getBySegment(segment: string, options?: PaginationOptions): Observable<Customer[]> {
    return this.getBySegmentPaged(segment, options).pipe(
      map(result => result.data)
    );
  }

  getBySegmentPaged(segment: string, options?: PaginationOptions): Observable<CustomerPagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(`${this.baseUrl}/customers/segment/${encodeURIComponent(segment)}`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes por segmento'); return throwError(() => err); })
    );
  }

  getByStatus(status: string, options?: PaginationOptions): Observable<Customer[]> {
    return this.getByStatusPaged(status, options).pipe(
      map(result => result.data)
    );
  }

  getByStatusPaged(status: string, options?: PaginationOptions): Observable<CustomerPagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(`${this.baseUrl}/customers/status/${encodeURIComponent(status)}`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes por status'); return throwError(() => err); })
    );
  }

  getByPersonType(personType: string, options?: PaginationOptions): Observable<Customer[]> {
    return this.getByPersonTypePaged(personType, options).pipe(
      map(result => result.data)
    );
  }

  getByPersonTypePaged(personType: string, options?: PaginationOptions): Observable<CustomerPagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(`${this.baseUrl}/customers/person-type/${encodeURIComponent(personType)}`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes por tipo de pessoa'); return throwError(() => err); })
    );
  }

  getInactive(options?: PaginationOptions): Observable<Customer[]> {
    return this.getInactivePaged(options).pipe(
      map(result => result.data)
    );
  }

  getInactivePaged(options?: PaginationOptions): Observable<CustomerPagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(`${this.baseUrl}/customers/inactive`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes inativos'); return throwError(() => err); })
    );
  }

  getBlocked(options?: PaginationOptions): Observable<Customer[]> {
    return this.getBlockedPaged(options).pipe(
      map(result => result.data)
    );
  }

  getBlockedPaged(options?: PaginationOptions): Observable<CustomerPagedResult> {
    const params = this.buildPaginationParams(options);
    return this.http.get<any>(`${this.baseUrl}/customers/blocked`, { params }).pipe(
      map(response => this.mapPagedResponse(response)),
      catchError(err => { this.errorService.showError('Erro ao buscar clientes bloqueados'); return throwError(() => err); })
    );
  }

  // ===== Exportação Excel =====

  /** Lista as colunas disponíveis para exportação (GET /customers/export/columns) */
  getExportColumns(): Observable<{ key: string; label: string }[]> {
    return this.http.get<{ key: string; label: string }[]>(`${this.baseUrl}/customers/export/columns`).pipe(
      catchError(err => { this.errorService.showError('Erro ao carregar colunas para exportação'); return throwError(() => err); })
    );
  }

  /**
   * Exporta clientes para Excel (POST /customers/export).
   * Body: { columns: string[]; status?: string | null; category?: string | null; searchTerm?: string | null }
   */
  exportToExcel(body: {
    columns: string[];
    status?: string | null;
    category?: string | null;
    searchTerm?: string | null;
  }): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/customers/export`, body, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map(response => {
        const blob = response.body;
        if (!blob) throw new Error('Resposta vazia do servidor');
        const contentType = response.headers.get('content-type') ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        if (!blob.type || !blob.type.includes('spreadsheet')) {
          return new Blob([blob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        }
        return blob;
      }),
      catchError(err => {
        if (err.error instanceof Blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            try {
              const errorText = JSON.parse(reader.result as string);
              this.errorService.showErrorMessage(errorText.message || 'Erro ao gerar planilha Excel');
            } catch {
              this.errorService.showErrorMessage('Erro ao gerar planilha Excel');
            }
          };
          reader.readAsText(err.error);
        } else {
          this.errorService.showErrorMessage('Erro ao gerar planilha Excel');
        }
        return throwError(() => err);
      })
    );
  }

  // ===== Blob de Customer (upload/list/download) =====
  uploadBlob(customerId: number, file: File): Observable<{ success: boolean; url: string; fileName: string; container: string }>
  {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const headers = new HttpHeaders();

    const prefix = `customer_${customerId}`;
    const uploadUrl = `${this.blobBaseUrl}/upload?prefix=${encodeURIComponent(prefix)}`;

    return this.http.post<{ success: boolean; url: string; fileName: string; container: string }>(uploadUrl, formData, { headers }).pipe(
      catchError(err => { this.errorService.showError('Erro ao fazer upload de arquivo'); return throwError(() => err); })
    );
  }

  listBlobs(customerId: number): Observable<string[]>
  {
    const prefix = `customer_${customerId}`;
    const params = new HttpParams().set('prefix', prefix);
    return this.http.get<string[]>(`${this.blobBaseUrl}/list`, { params }).pipe(
      catchError(err => { this.errorService.showError('Erro ao listar arquivos'); return throwError(() => err); })
    );
  }

  downloadBlob(customerId: number, fileName: string): Observable<Blob>
  {
    let params = new HttpParams().set('fileName', fileName);
    params = params.set('prefix', `customer_${customerId}`);
    return this.http.get(`${this.blobBaseUrl}/download`, { params, responseType: 'blob' }).pipe(
      catchError(err => { this.errorService.showError('Erro ao baixar arquivo'); return throwError(() => err); })
    );
  }

  // ===== Anexos de Customer (API de domínio) =====
  listAttachments(customerId: number): Observable<CustomerAttachment[]>
  {
    return this.http.get<CustomerAttachment[]>(`${this.baseUrl}/customers/${customerId}/attachments`).pipe(
      catchError(err => { this.errorService.showError('Erro ao listar anexos do cliente'); return throwError(() => err); })
    );
  }

  getAttachment(id: number): Observable<CustomerAttachment>
  {
    return this.http.get<CustomerAttachment>(`${this.baseUrl}/customer-attachments/${id}`).pipe(
      catchError(err => { this.errorService.showError('Erro ao buscar anexo'); return throwError(() => err); })
    );
  }

  createAttachment(payload: CustomerAttachmentCreate): Observable<CustomerAttachment>
  {
    return this.http.post<CustomerAttachment>(`${this.baseUrl}/customer-attachments`, payload).pipe(
      catchError(err => { this.errorService.showError('Erro ao criar anexo'); return throwError(() => err); })
    );
  }

  updateAttachment(id: number, payload: CustomerAttachmentUpdate): Observable<CustomerAttachment>
  {
    return this.http.put<CustomerAttachment>(`${this.baseUrl}/customer-attachments/${id}`, payload).pipe(
      catchError(err => { this.errorService.showError('Erro ao atualizar anexo'); return throwError(() => err); })
    );
  }

  removeAttachment(id: number): Observable<void>
  {
    return this.http.delete<void>(`${this.baseUrl}/customer-attachments/${id}`).pipe(
      catchError(err => { this.errorService.showError('Erro ao remover anexo'); return throwError(() => err); })
    );
  }

  uploadAttachment(customerId: number, file: File, meta?: { category?: string; description?: string; uploadedBy?: string }): Observable<CustomerAttachment>
  {
    type BlobUploadResponse = { success: boolean; url: string; fileName: string };
    const formData = new FormData();
    
    // Se tem categoria, adiciona como prefixo no nome do arquivo
    let fileName = file.name;
    if (meta?.category) {
      const categoryPrefix = this.getCategoryPrefix(meta.category);
      fileName = `${categoryPrefix}_${file.name}`;
    }
    
    formData.append('file', file, fileName);
    const headers = new HttpHeaders();

    const prefix = `customer_${customerId}`;
    const uploadUrl = `${this.blobBaseUrl}/upload?prefix=${encodeURIComponent(prefix)}`;

    return this.http.post<BlobUploadResponse>(uploadUrl, formData, { headers }).pipe(
      switchMap(res => {
        if (!res?.success || !res.url) {
          throw new Error('Falha no upload para Blob');
        }
        const registerPayload: any = {
          customerId: customerId,
          fileName: fileName, // Nome do arquivo com prefixo (CNH_, RG_, etc)
          filePath: res.url, // URL completa do blob
          originalFileName: fileName,
          contentType: file.type || 'application/octet-stream',
          fileSize: file.size,
          category: meta?.category,
          description: meta?.description,
          uploadedBy: meta?.uploadedBy || 'system',
          active: true
        };
        return this.createAttachment(registerPayload);
      }),
      catchError(err => { this.errorService.showError('Erro ao fazer upload de anexo'); return throwError(() => err); })
    );
  }

  private getCategoryPrefix(category: string): string {
    const prefixMap: { [key: string]: string } = {
      'CNH': 'CNH',
      'RG': 'RG',
      'COMPROVANTE_RESIDENCIA': 'Comprovante_Residencia',
      'OUTROS': 'Outros'
    };
    return prefixMap[category] || category;
  }

  getAttachmentStatistics(customerId: number): Observable<any>
  {
    return this.http.get<any>(`${this.baseUrl}/customers/${customerId}/attachments/statistics`).pipe(
      map(response => response?.statistics ?? response),
      catchError(err => { this.errorService.showError('Erro ao buscar estatísticas de anexos'); return throwError(() => err); })
    );
  }

  // ===== Endereços de Customer =====
  getAddresses(customerId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/customers/${customerId}/addresses`).pipe(
      catchError(err => { this.errorService.showError('Erro ao buscar endereços do cliente'); return throwError(() => err); })
    );
  }

 
  private mapPagedResponse(response: any): CustomerPagedResult {
    // Se a resposta já é um array (formato antigo), converter para formato paginado
    if (Array.isArray(response)) {
      return {
        data: response.map(c => this.mapFromCustomerDTO(c)),
        page: 1,
        pageSize: response.length,
        totalRecords: -1, // Não disponível no formato antigo
        totalPages: -1,   // Não disponível no formato antigo
        hasNextPage: false,
        hasPreviousPage: false
      };
    }

    // Formato novo com Count (PascalCase): Items, Page, PageSize, TotalItems, TotalPages
    const items = Array.isArray(response.Items) ? response.Items : 
                  (Array.isArray(response.items) ? response.items : 
                   (Array.isArray(response.data) ? response.data : []));
    
    // Mapear campos (suporta tanto PascalCase quanto camelCase)
    const page = response.Page ?? response.page ?? 1;
    const pageSize = response.PageSize ?? response.pageSize ?? items.length;
    const totalItems = response.TotalItems ?? response.totalItems ?? 
                      (response.TotalRecords ?? response.totalRecords ?? -1);
    const totalPages = response.TotalPages ?? response.totalPages ?? -1;
    
    // Calcular hasNextPage e hasPreviousPage se não vierem na resposta
    const hasNextPage = response.HasNextPage ?? response.hasNextPage ?? 
                       (totalPages > 0 ? page < totalPages : false);
    const hasPreviousPage = response.HasPreviousPage ?? response.hasPreviousPage ?? 
                           (page > 1);
    
    return {
      data: items.map((c: any) => this.mapFromCustomerDTO(c)),
      page: page,
      pageSize: pageSize,
      totalRecords: totalItems >= 0 ? totalItems : -1,
      totalPages: totalPages >= 0 ? totalPages : -1,
      hasNextPage: hasNextPage,
      hasPreviousPage: hasPreviousPage
    };
  }

  private mapFromCustomerDTO(dto: any): Customer {
    const addresses = Array.isArray(dto?.Addresses) ? dto.Addresses : (Array.isArray(dto?.addresses) ? dto.addresses : []);
    const contactsRaw = Array.isArray(dto?.Contacts) ? dto.Contacts : (Array.isArray(dto?.contacts) ? dto.contacts : []);

    // Pega endereço padrão ou primeiro endereço
    const defaultAddress = addresses.find((a: any) => a.IsDefault || a.isDefault) || addresses[0];
    // Pega contato padrão ou primeiro contato
    const defaultContact = contactsRaw.find((c: any) => c.IsDefault || c.isDefault) || contactsRaw[0];
    
    const contacts: CustomerContact[] = contactsRaw.map((contact: any) => ({
      contactType: contact?.ContactType || contact?.contactType || 'Principal',
      contactName: contact?.ContactName || contact?.contactName || '',
      phoneNumber: contact?.PhoneNumber || contact?.phoneNumber || '',
      secondaryPhoneNumber: contact?.SecondaryPhoneNumber || contact?.secondaryPhoneNumber || undefined,
      email: contact?.Email || contact?.email || undefined,
      isDefault: !!(contact?.IsDefault ?? contact?.isDefault)
    }));

    return {
      id: dto.Id || 0,
      personType: dto.PersonType || 'Juridica',
      name: dto.Name || dto.CompanyName || '', // Suporta ambos para compatibilidade temporária
      tradeName: dto.TradeName,
      documentNumber: dto.DocumentNumber || '',
      stateRegistration: dto.StateRegistration,
      driverLicenseNumber: dto.DriverLicenseNumber,
      driverLicenseExpiryDate: dto.DriverLicenseExpiryDate,
      street: defaultAddress?.Street,
      number: defaultAddress?.Number,
      complement: defaultAddress?.Complement,
      neighborhood: defaultAddress?.Neighborhood,
      city: defaultAddress?.City,
      state: defaultAddress?.State,
      zipCode: defaultAddress?.ZipCode,
      primaryPhone: defaultContact?.PhoneNumber,
      secondaryPhone: defaultContact?.SecondaryPhoneNumber,
      email: dto.Email || defaultContact?.Email,
      website: dto.Website,
      contactPersonName: defaultContact?.ContactName,
      contactPersonPhone: defaultContact?.PhoneNumber,
      contactPersonEmail: defaultContact?.Email,
      contacts,
      notes: dto.Notes,
      active: dto.Status === 'Ativo',
      blocked: dto.Status !== 'Ativo', // Se não é Ativo, então está Bloqueado (inclui Inativo e Bloqueado)
      customerCategory: dto.CustomerCategory,
      status: dto.Status,
      bankName: dto.BankName ?? null,
      agency: dto.Agency ?? null,
      accountType: dto.AccountType ?? null,
      accountNumber: dto.AccountNumber ?? null,
      accountHolderName: dto.AccountHolderName ?? null,
      pixKey: dto.PIXKey ?? null
    };
  }

  private extractCustomerPayload(response: any): any {
    if (!response || typeof response !== 'object') {
      return response;
    }

    let current = response;
    while (current && typeof current === 'object') {
      const next = current.Customer
        ?? current.customer
        ?? current.Data
        ?? current.data
        ?? current.Result
        ?? current.result
        ?? current.Value
        ?? current.value;

      if (!next || next === current) {
        break;
      }

      current = next;
    }

    return current;
  }

  private mapToCustomerDTO(payload: any): any {
    // Se blocked=true, status é "Bloqueado"; se active=true, status é "Ativo"; caso contrário "Inativo"
    let status = 'Ativo';
    if (payload?.blocked === true) {
      status = 'Bloqueado';
    } else if (payload?.active === false) {
      status = 'Inativo';
    } else if (payload?.status) {
      status = payload.status;
    }
    
    return {
      // Campos obrigatórios
      PersonType: payload?.personType || 'Juridica',
      Name: payload?.name || payload?.companyName || '', // Suporta ambos para compatibilidade temporária
      DocumentNumber: payload?.documentNumber || '',
      Status: status,
      
      // Campos opcionais
      TradeName: payload?.tradeName,
      StateRegistration: payload?.stateRegistration,
      DriverLicenseNumber: payload?.driverLicenseNumber || null,
      // "" quebra DateTime? no backend (JsonException → 500)
      DriverLicenseExpiryDate: this.toNullableDate(payload?.driverLicenseExpiryDate),
      FoundationDate: this.toNullableDate(payload?.foundationDate),
      BusinessSegment: payload?.businessSegment,
      Email: payload?.email,
      Website: payload?.website,
      PaymentTerms: payload?.paymentTerms,
      CreditLimit: payload?.creditLimit,
      PaymentDays: payload?.paymentDays,
      BankName: payload?.bankName,
      Agency: payload?.agency,
      AccountType: payload?.accountType,
      AccountNumber: payload?.accountNumber,
      AccountHolderName: payload?.accountHolderName,
      PIXKey: payload?.pixKey,
      CustomerCategory: payload?.customerCategory,
      Notes: payload?.notes,
      
      // Coleções (estruturas básicas para endereço e contato)
      Addresses: payload?.street ? [{
        CustomerId: 0,
        AddressType: 'Principal',
        Street: payload.street,
        Number: payload.number,
        Complement: payload.complement,
        Neighborhood: payload.neighborhood,
        City: payload.city,
        State: payload.state,
        ZipCode: payload.zipCode,
        Country: 'BR',
        IsDefault: true
      }] : [],
      
      Contacts: this.mapContactsToDto(payload),
      
      Attachments: []
    };
  }

  /** Converte string vazia / inválida em null para campos DateTime? do backend. */
  private toNullableDate(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    return raw;
  }

  private mapContactsToDto(payload: any): any[] {
    if (Array.isArray(payload?.contacts) && payload.contacts.length > 0) {
      return payload.contacts
        .filter((contact: any) => !!(contact?.contactName || '').trim() && !!(contact?.phoneNumber || '').trim())
        .map((contact: any) => ({
          CustomerId: 0,
          ContactType: contact.contactType || 'Principal',
          ContactName: contact.contactName,
          PhoneNumber: contact.phoneNumber,
          SecondaryPhoneNumber: contact.secondaryPhoneNumber || undefined,
          Email: undefined,
          IsEmergencyContact: false,
          IsDefault: !!contact.isDefault
        }));
    }

    if (payload?.primaryPhone || payload?.email) {
      return [{
        CustomerId: 0,
        ContactType: 'Principal',
        ContactName: payload.name || payload.companyName || 'Contato Principal',
        PhoneNumber: payload.primaryPhone,
        SecondaryPhoneNumber: payload.secondaryPhone,
        Email: payload.email,
        IsEmergencyContact: false,
        IsDefault: true
      }];
    }

    return [];
  }
}


